// The live feed client — Phase 4.
//
// A WebSocket is easy. Everything below is about the ways one stops working WITHOUT
// telling you, because each of them presents to an operator as "the dashboard is
// stale" and to a developer as "works on my machine".
//
//   1. The socket drops           -> reconnect, with backoff so a restarting server
//                                    is not hammered by every dashboard at once.
//   2. It reconnects              -> RE-FETCH. Anything that happened during the gap
//                                    was never delivered, and no amount of socket
//                                    correctness recovers it.
//   3. The laptop sleeps          -> the OS still calls the socket OPEN and it will
//                                    never deliver another byte. Only a watchdog on
//                                    SILENCE catches this.
//   4. The session expired        -> the server closes with 1008. Reconnecting in a
//                                    loop makes an expired licence look like a
//                                    network fault, forever. Probe REST first.
//
// The server pings every 30s, so healthy traffic is under 30s apart. Anything past
// 75s of total silence is a dead link.
import type { Session } from '../api/client';

export type LiveStatus = 'connecting' | 'live' | 'offline';

export interface LiveHandlers {
    /** A reading arrived. */
    onReading: (reading: unknown) => void;
    /** Connected (or reconnected). ALWAYS re-hydrate here — see (2) above. */
    onResync: () => void;
    onStatus: (status: LiveStatus) => void;
    /** The session is over. Carries the server's reason when it gave one. */
    onSessionEnded: (reason: string | null) => void;
}

const BACKOFF_BASE_MS = 1_000;
const BACKOFF_CAP_MS = 30_000;
/** Longer than the server's 30s ping, with room for one missed beat. */
const SILENCE_LIMIT_MS = 75_000;
const WATCHDOG_EVERY_MS = 15_000;
/** After a resume, a socket quiet for longer than this is assumed dead. */
const RESUME_STALE_MS = 45_000;

export interface LiveFeed {
    stop: () => void;
}

export function startLiveFeed(session: Session, handlers: LiveHandlers): LiveFeed {
    let ws: WebSocket | null = null;
    let attempt = 0;
    let lastMessageAt = Date.now();
    let stopped = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const socketUrl = () => {
        const base = session.baseUrl.replace(/\/+$/, '');
        const http = base || window.location.origin;
        const url = new URL('/ws/readings', http);
        url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
        // Browsers cannot set headers on a WebSocket handshake, so the token has to
        // travel in the query string. The server redacts it from its logs; anything
        // in front of it must not log query strings for this path.
        url.searchParams.set('token', session.token);
        return url.toString();
    };

    /**
     * Is the session still valid?
     *
     * Called before reconnecting after an abnormal close. Without it, an expired
     * licence and a flaky network are indistinguishable from the client's side, and
     * the dashboard reconnect-loops instead of telling the operator what happened.
     */
    async function sessionStillValid(): Promise<{ ok: boolean; reason: string | null }> {
        try {
            const base = session.baseUrl.replace(/\/+$/, '');
            const res = await fetch(`${base}/api/v1/operator/session`, {
                headers: { Authorization: `Bearer ${session.token}` },
            });
            if (res.status === 401) {
                const body = (await res.json().catch(() => ({}))) as { message?: string };
                return { ok: false, reason: body.message ?? null };
            }
            return { ok: true, reason: null };
        } catch {
            // The probe itself failed, which is a network problem, not an auth one.
            // Assume the session is fine and let the backoff do its work.
            return { ok: true, reason: null };
        }
    }

    function scheduleReconnect() {
        if (stopped) return;
        // Exponential with a 30s cap. Uncapped, a dashboard left open overnight
        // against a dead server waits hours before its next try.
        const delay = Math.min(BACKOFF_BASE_MS * 2 ** attempt, BACKOFF_CAP_MS);
        attempt += 1;
        reconnectTimer = setTimeout(connect, delay);
    }

    function connect() {
        if (stopped) return;
        handlers.onStatus('connecting');
        try {
            ws = new WebSocket(socketUrl());
        } catch {
            scheduleReconnect();
            return;
        }

        ws.onopen = () => {
            attempt = 0;
            lastMessageAt = Date.now();
            handlers.onStatus('live');
            // THE resync strategy, in one line. No event replay, no sequence
            // numbers: whatever arrived during the outage is fetched over REST.
            handlers.onResync();
        };

        ws.onmessage = (ev) => {
            lastMessageAt = Date.now();
            try {
                const msg = JSON.parse(String(ev.data)) as { type?: string; reading?: unknown };
                if (msg.type === 'READING_NEW' && msg.reading) handlers.onReading(msg.reading);
            } catch {
                // A frame we cannot parse is not a reason to tear down a working feed.
            }
        };

        ws.onerror = () => {
            /* onclose always follows; handled there. */
        };

        ws.onclose = (ev) => {
            handlers.onStatus('offline');
            if (stopped) return;
            // 1006 abnormal, 1008 policy violation. Both can mean "your session is
            // over", so ask before assuming it was the network.
            if (ev.code === 1006 || ev.code === 1008) {
                void sessionStillValid().then(({ ok, reason }) => {
                    if (!ok) {
                        stopped = true;
                        handlers.onSessionEnded(reason);
                        return;
                    }
                    scheduleReconnect();
                });
                return;
            }
            scheduleReconnect();
        };
    }

    // ─── The watchdog ───────────────────────────────────────────────────
    // A socket the OS still reports as OPEN can be silently dead — the classic case
    // is a laptop resuming from sleep. readyState says nothing useful; only the
    // absence of traffic does.
    const watchdog = setInterval(() => {
        if (stopped || !ws) return;
        if (ws.readyState === WebSocket.OPEN && Date.now() - lastMessageAt > SILENCE_LIMIT_MS) {
            // close() triggers onclose, which reconnects through the normal path.
            ws.close();
        }
    }, WATCHDOG_EVERY_MS);

    const onWake = () => {
        if (stopped) return;
        if (document.visibilityState === 'hidden') return;
        if (ws && ws.readyState === WebSocket.OPEN && Date.now() - lastMessageAt > RESUME_STALE_MS) {
            ws.close();
        } else if (!ws || ws.readyState === WebSocket.CLOSED) {
            if (reconnectTimer) clearTimeout(reconnectTimer);
            attempt = 0;
            connect();
        }
    };
    window.addEventListener('online', onWake);
    document.addEventListener('visibilitychange', onWake);

    connect();

    return {
        stop() {
            stopped = true;
            if (reconnectTimer) clearTimeout(reconnectTimer);
            clearInterval(watchdog);
            window.removeEventListener('online', onWake);
            document.removeEventListener('visibilitychange', onWake);
            ws?.close();
            ws = null;
        },
    };
}
